type UiState = Record<string, Record<string, any>>;

interface RouteState {
  q?: string;
  sort?: string;
  page?: number;
  instock?: boolean;
  category?: string;
  author?: string;
  format?: string;
  publicationdate?: string;
  pricerange?: string;
  agerange?: string;
  producttype?: string;
  brand?: string;
  colour?: string;
  packsize?: string;
  booktype?: string;
  series?: string;
}

const joinValues = (values?: string[]): string | undefined =>
  values?.join(';');

const splitValues = (value?: string): string[] | undefined =>
  value?.split(';');

/**
 * Parse the current URL's query string into a RouteState.
 */
const parseUrl = (): RouteState => {
  const params = new URLSearchParams(window.location.search);
  const route: RouteState = {};

  if (params.has('q')) route.q = params.get('q') ?? undefined;
  if (params.has('sort')) route.sort = params.get('sort') ?? undefined;
  if (params.has('page')) route.page = Number(params.get('page')) || undefined;
  if (params.has('instock')) route.instock = params.get('instock') === 'true';
  if (params.has('category')) route.category = params.get('category') ?? undefined;
  if (params.has('author')) route.author = params.get('author') ?? undefined;
  if (params.has('format')) route.format = params.get('format') ?? undefined;
  if (params.has('publicationdate')) route.publicationdate = params.get('publicationdate') ?? undefined;
  if (params.has('pricerange')) route.pricerange = params.get('pricerange') ?? undefined;
  if (params.has('agerange')) route.agerange = params.get('agerange') ?? undefined;
  if (params.has('producttype')) route.producttype = params.get('producttype') ?? undefined;
  if (params.has('brand')) route.brand = params.get('brand') ?? undefined;
  if (params.has('colour')) route.colour = params.get('colour') ?? undefined;
  if (params.has('packsize')) route.packsize = params.get('packsize') ?? undefined;
  if (params.has('booktype')) route.booktype = params.get('booktype') ?? undefined;
  if (params.has('series')) route.series = params.get('series') ?? undefined;

  return route;
};

/**
 * Serialize a RouteState to a URL query string.
 */
const routeToUrl = (routeState: RouteState): string => {
  const params = new URLSearchParams();

  if (routeState.q) params.set('q', routeState.q);
  if (routeState.sort) params.set('sort', routeState.sort);
  if (routeState.page && routeState.page > 1) params.set('page', String(routeState.page));
  if (routeState.instock) params.set('instock', 'true');
  if (routeState.category) params.set('category', routeState.category);
  if (routeState.author) params.set('author', routeState.author);
  if (routeState.format) params.set('format', routeState.format);
  if (routeState.publicationdate) params.set('publicationdate', routeState.publicationdate);
  if (routeState.pricerange) params.set('pricerange', routeState.pricerange);
  if (routeState.agerange) params.set('agerange', routeState.agerange);
  if (routeState.producttype) params.set('producttype', routeState.producttype);
  if (routeState.brand) params.set('brand', routeState.brand);
  if (routeState.colour) params.set('colour', routeState.colour);
  if (routeState.packsize) params.set('packsize', routeState.packsize);
  if (routeState.booktype) params.set('booktype', routeState.booktype);
  if (routeState.series) params.set('series', routeState.series);

  const qs = params.toString();
  return qs ? `${window.location.pathname}?${qs}` : window.location.pathname;
};

const stateMapping = {
  stateToRoute(uiState: UiState, indexName: string): RouteState {
    const state = uiState[indexName] || {};
    return {
      q: state.query,
      instock: state.toggle?.inStock,
      page: state.page,
      sort: state.sortBy,
      author: joinValues(state.refinementList?.author),
      category: joinValues(
        state.hierarchicalMenu?.['hierarchicalCategories.lvl0'],
      ),
      series: joinValues(state.refinementList?.seriesCode),
      format: joinValues(state.refinementList?.format),
      publicationdate: joinValues(
        state.refinementList?.publicationDateRange,
      ),
      pricerange: joinValues(state.refinementList?.priceRange),
      agerange: joinValues(state.refinementList?.ageRange),
      producttype: joinValues(state.refinementList?.productType),
      brand: joinValues(state.refinementList?.brand),
      colour: joinValues(state.refinementList?.colour),
      packsize: joinValues(state.refinementList?.packSize),
      booktype: joinValues(state.refinementList?.bookType),
    };
  },
  routeToState(routeState: RouteState, indexName: string): UiState {
    return {
      [indexName]: {
        query: routeState.q,
        refinementList: {
          author: splitValues(routeState.author),
          seriesCode: splitValues(routeState.series),
          format: splitValues(routeState.format),
          publicationDateRange: splitValues(routeState.publicationdate),
          priceRange: splitValues(routeState.pricerange),
          ageRange: splitValues(routeState.agerange),
          productType: splitValues(routeState.producttype),
          brand: splitValues(routeState.brand),
          colour: splitValues(routeState.colour),
          packSize: splitValues(routeState.packsize),
          bookType: splitValues(routeState.booktype),
        },
        page: routeState.page,
        sortBy: routeState.sort,
        toggle: { inStock: routeState.instock },
        hierarchicalMenu: {
          'hierarchicalCategories.lvl0': splitValues(routeState.category),
        },
      },
    };
  },
};

/**
 * Build the initial UI state from the current URL so InstantSearch
 * hydrates with the correct query/filters on page load.
 */
export const getInitialUiState = (indexName: string): UiState => {
  const routeState = parseUrl();
  return stateMapping.routeToState(routeState, indexName);
};

/**
 * Create a custom router + stateMapping for InstantSearch that uses
 * our flat query-param format (?q=, ?sort=, ?author=, etc.).
 *
 * The router captures the initial URL at creation time so that the first
 * write() cannot accidentally blank a deep-linked query string. After
 * the first user-driven state change, writes switch to pushState.
 */
export const createRouting = (indexName: string) => {
  // Snapshot the URL at mount time — this is the deep-linked state.
  const initialUrl = window.location.href;
  let isFirstWrite = true;
  let popstateHandler: (() => void) | null = null;

  return {
    router: {
      read(): RouteState {
        return parseUrl();
      },
      write(routeState: RouteState): void {
        const url = routeToUrl(routeState);

        if (isFirstWrite) {
          // On the very first write, InstantSearch is syncing its internal
          // state to the URL. Use replaceState so we don't push a duplicate
          // history entry, and only replace if the URL actually changed
          // (avoids stripping the deep-linked params).
          isFirstWrite = false;
          if (url !== initialUrl) {
            window.history.replaceState(routeState, '', url);
          }
          return;
        }

        window.history.pushState(routeState, '', url);
      },
      createURL(routeState: RouteState): string {
        return routeToUrl(routeState);
      },
      onUpdate(callback: (routeState: RouteState) => void): void {
        popstateHandler = () => callback(parseUrl());
        window.addEventListener('popstate', popstateHandler);
      },
      dispose(): void {
        if (popstateHandler) {
          window.removeEventListener('popstate', popstateHandler);
          popstateHandler = null;
        }
      },
    },
    stateMapping: {
      stateToRoute(uiState: UiState): RouteState {
        return stateMapping.stateToRoute(uiState, indexName);
      },
      routeToState(routeState: RouteState): UiState {
        return stateMapping.routeToState(routeState, indexName);
      },
    },
  };
};
